/**
 * Identity block at the top of the tenant dashboard's mobile drawer.
 *
 * Carries a Settings entry as well as the name and email. On a phone the
 * drawer is already open when a tenant wants Settings, so making them close it
 * and find the navbar account menu is a detour; that menu keeps its entry too.
 *
 * Extracted from Dashboard so the layout can be rendered and measured on its
 * own — Dashboard itself needs a live tenant session to mount.
 */
import { Settings, Users } from "lucide-react";
import { SheetClose } from "@/components/ui/sheet";

export function TenantDrawerHeader({
  displayName,
  email,
  settingsLabel,
  onOpenSettings,
}: {
  displayName: string;
  email: string;
  settingsLabel: string;
  onOpenSettings: () => void;
}) {
  return (
    // pr-12 keeps the identity clear of the sheet's own close button, which is
    // absolutely positioned in this corner. shrink-0 is belt-and-braces: the
    // scrolling nav beside it already absorbs any overflow, but this block holds
    // its height even if that container changes.
    <div className="shrink-0 bg-primary text-primary-foreground px-6 pt-7 pb-5 pr-12 space-y-4">
      <div className="flex items-center gap-4">
        <div className="h-12 w-12 shrink-0 rounded-full bg-primary-foreground/20 flex items-center justify-center">
          <Users className="h-6 w-6" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-semibold leading-tight truncate">{displayName}</p>
          <p className="text-sm leading-snug text-primary-foreground/80 truncate">
            {email}
          </p>
        </div>
      </div>
      {/* Second way in, not a replacement: Settings still lives in the top
          navbar's account menu. On mobile that menu is one more tap away than
          this drawer, which is already open. */}
      <SheetClose asChild>
        <button
          type="button"
          onClick={onOpenSettings}
          className="w-full flex items-center gap-2 rounded-lg bg-primary-foreground/15 hover:bg-primary-foreground/25 px-3 py-2 text-sm font-medium transition-colors"
        >
          <Settings className="h-4 w-4 shrink-0" />
          {settingsLabel}
        </button>
      </SheetClose>
    </div>
  );
}

export default TenantDrawerHeader;
